import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  createBookingReservationGroup,
  selectBooking,
  setBookingCustomer,
} from "../features/booking/bookingSlice";
import { type IbookingCustomer } from "../features/booking/bookingInterface";
import { countries, getCountry } from "../helpers/countries";
import InputField from "../components/Form/InputField";
import SelectField from "../components/Form/SelectField";
import Textarea from "../components/Form/Textarea";
import CheckBoxField from "../components/Form/CheckBoxField";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GTMTrackingSelectPage } from "../helpers/GTMTracking";
import PhoneField from "../components/Form/PhoneField";
import { selectConfig, setReturnUrl } from "../features/config/configSlice";
import { openUrl } from "../helpers/payment";
import ContentOverlay from "../components/ContentOverlay";
import ArrowSmall from "../components/Svg/ArrowSmall";
import Button from "../components/Button";
import { Trans, useTranslation } from "react-i18next";

function Details() {
  const dispatch = useAppDispatch();
  const booking = useAppSelector(selectBooking);
  const config = useAppSelector(selectConfig);
  const navigate = useNavigate();
  const location = useLocation();
  const [isValid, setIsValid] = useState<boolean>(false);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [overlayContent, setOverlayContent] = useState<string>("");
  const { t } = useTranslation();
  const countryOptions: { [key: string]: string } = countries();

  const InitialCustomerState = {
    Email: "",
    FirstName: "",
    LastName: "",
    Telephone: "",
    AddressLine1: "",
    AddressLine2: "",
    City: "",
    PostalCode: "",
    StateCode: "",
    NationalityCode: getCountry() || "",
    Notes: "",
    SendMarketingEmails: false,
    Terms: false,
  };
  const [customerState, setCustomerState] = useState<IbookingCustomer>(
    booking.Customer ?? InitialCustomerState
  );
  const initialValidState = {
    FirstName: null,
    LastName: null,
    Telephone: null,
    Email: null,
    NationalityCode: null,
    Terms: null,
  };
  const [validationState, setValidationState] = useState<{
    [key: string]: boolean | null;
  }>(initialValidState);

  useEffect(() => {
    dispatch(setBookingCustomer(customerState));
  }, [customerState, dispatch]);

  useEffect(() => {
    let isValid = true;
    Object.keys(validationState).forEach((name) => {
      if (validationState[name] === null || validationState[name] === false) {
        isValid = false;
      }
    });
    setIsValid(isValid);
  }, [validationState]);

  const next = () => {
    if (validateCustomer()) {
      // GTM DATALAYER
      GTMTrackingSelectPage(location.pathname);
      navigate("/payment");
    }
  };

  const nextCreateReservation = async () => {
    if (validateCustomer()) {
      setOverlayContent(t("scenes.details.creatingReservation"));
      setShowOverlay(true);

      await dispatch(createBookingReservationGroup({ data: null }))
        .then((response) => {
          if (response.payload.Reservations.length > 0) {
            setOverlayContent(t("scenes.details.reservationCreated"));
            setTimeout(() => {
              navigate("/success");
            }, 1000);
          } else {
            setOverlayContent(t("scenes.details.somethingWentWrong"));
            setTimeout(() => {
              setShowOverlay(false);
            }, 2000);
          }
        })
        .catch(() => {
          setOverlayContent(
            `[error:4501] ${t("scenes.details.somethingWentWrong")}`
          );
          setTimeout(() => {
            setShowOverlay(false);
          }, 2000);
        });
    }
  };

  // Create reservation group and return to return url
  const nextReturnUrl = async () => {
    if (validateCustomer() && config.ReturnUrl !== null) {
      setOverlayContent(t("scenes.details.creatingReservation"));
      setShowOverlay(true);

      await dispatch(createBookingReservationGroup({ data: null }))
        .then((response) => {
          if (response.payload.Reservations.length > 0) {
            const returnUrl = config.ReturnUrl;
            dispatch(setReturnUrl(null));
            setOverlayContent(t("scenes.details.reservationCreated"));
            setTimeout(() => {
              openUrl(
                returnUrl +
                  "?reservationId=" +
                  response.payload.Reservations[0].Id
              );
              setOverlayContent(t("scenes.details.finishPaymentAtKiosk"));
              return false;
            }, 1000);
          } else {
            setOverlayContent(t("scenes.details.somethingWentWrong"));
            setTimeout(() => {
              setShowOverlay(false);
            }, 2000);
          }
        })
        .catch(() => {
          setOverlayContent(
            `[error:4501] ${t("scenes.details.somethingWentWrong")}`
          );
          setTimeout(() => {
            setShowOverlay(false);
          }, 2000);
        });
    }
  };

  const handleValueCallback = (obj: { [key: string]: string | boolean }) => {
    setCustomerState((previousState) => ({ ...previousState, ...obj }));
  };

  const handleValidateCallBack = (obj: { [key: string]: boolean | null }) => {
    setValidationState((previousState) => ({ ...previousState, ...obj }));
  };

  const validateCustomer = () => {
    let isValid = true;
    let obj: { [key: string]: boolean | null } = {};
    Object.keys(validationState).forEach((name) => {
      if (validationState[name] === null) {
        obj[name as keyof typeof obj] = false;
        isValid = false;
      } else if (validationState[name] === false) {
        isValid = false;
      }
    });

    setValidationState({ ...validationState, ...obj });

    if (!isValid) {
      return false;
    }
    return true;
  };

  return (
    <>
      <div className="absolute top-0 left-0 flex flex-col w-full h-full">
        <div className="flex-grow relative w-full">
          <motion.div
            initial={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
            animate={{ opacity: 1, y: "0px", transition: { duration: 0.4 } }}
            exit={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
            className="absolute inset-0 overflow-y-auto w-full px-4 py-4"
          >
            <div className="flex flex-col space-y-4 text-fluid-base bg-custom-100 rounded-lg p-4">
              <InputField
                type="text"
                label={t("scenes.details.firstName")}
                name="FirstName"
                value={customerState.FirstName}
                error={validationState.FirstName}
                validate={{ min: 1 }}
                errorMessages={{ min: t("scenes.details.firstNameRequired") }}
                valueCallback={handleValueCallback}
                validationCallBack={handleValidateCallBack}
              />

              <InputField
                type="text"
                label={t("scenes.details.lastName")}
                name="LastName"
                value={customerState.LastName}
                error={validationState.LastName}
                validate={{ min: 1 }}
                errorMessages={{ min: t("scenes.details.lastNameRequired") }}
                valueCallback={handleValueCallback}
                validationCallBack={handleValidateCallBack}
              />

              <InputField
                type="email"
                label={t("scenes.details.email")}
                name="Email"
                value={customerState.Email}
                error={validationState.Email}
                validate={{ min: 1, email: "" }}
                errorMessages={{
                  min: t("scenes.details.emailRequired"),
                  email: t("scenes.details.emailInvalid"),
                }}
                valueCallback={handleValueCallback}
                validationCallBack={handleValidateCallBack}
              />

              <PhoneField
                label={t("scenes.details.phone")}
                name="Telephone"
                value={customerState.Telephone}
                error={validationState.Telephone}
                validate={{ min: 9, phonenumber: true }}
                errorMessages={{
                  min: t("scenes.details.phoneMin"),
                  phonenumber: t("scenes.details.phoneInvalid"),
                }}
                valueCallback={handleValueCallback}
                validationCallBack={handleValidateCallBack}
              />

              <SelectField
                label={t("scenes.details.country")}
                placeholder={t("scenes.details.selectCountry")}
                name="NationalityCode"
                value={customerState.NationalityCode}
                error={validationState.NationalityCode}
                options={countryOptions}
                validate={{ required: true }}
                errorMessages={{
                  required: t("scenes.details.countryRequired"),
                }}
                valueCallback={handleValueCallback}
                validationCallBack={handleValidateCallBack}
              />

              <Textarea
                label={t("scenes.details.specialRequests")}
                name="Notes"
                value={customerState.Notes}
                callback={handleValueCallback}
              />

              <CheckBoxField
                label={
                  <Trans
                    i18nKey="scenes.details.terms"
                    components={{
                      firstLink: (
                        <Link
                          to="https://cityhub.com/terms-conditions/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        />
                      ),
                      secondLink: (
                        <Link
                          to="https://cityhub.com/privacy-statement/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        />
                      ),
                    }}
                  />
                }
                name="Terms"
                value={customerState.Terms}
                error={validationState.Terms}
                validate={{ required: true }}
                valueCallback={handleValueCallback}
                validationCallBack={handleValidateCallBack}
              />

              <CheckBoxField
                label={t("scenes.details.marketingEmails")}
                name="SendMarketingEmails"
                value={customerState.SendMarketingEmails}
                error={validationState.SendMarketingEmails}
                validate={{}}
                valueCallback={handleValueCallback}
                validationCallBack={handleValidateCallBack}
              />
            </div>

            <div className="mt-4">
              {booking.ReservationPrice.GrossValue === 0 && (
                <Button
                  text={t("scenes.details.createReservation")}
                  onClick={() => nextCreateReservation()}
                  suffix={<ArrowSmall className="size-fluid-4" />}
                  disabled={!isValid}
                />
              )}

              {booking.ReservationPrice.GrossValue > 0 && (
                <>
                  {(config.ReturnUrl === null || config.ReturnUrl === "") && (
                    <Button
                      text={t("cart.title.details")}
                      onClick={() => next()}
                      disabled={!isValid}
                    />
                  )}

                  {config.ReturnUrl !== null && config.ReturnUrl !== "" && (
                    // Create reservation and return to Return url
                    // Payment is done in kiosk or other application
                    <Button
                      text={t("cart.title.details")}
                      onClick={() => nextReturnUrl()}
                    />
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      <ContentOverlay show={showOverlay} content={overlayContent} />
    </>
  );
}

export default Details;
